// Gavion configuration (configuration.config.gavion) with defaults. Keep equal to the SQL gavion_config() defaults.
import { DEFAULT_BUSINESS_DAYS, DEFAULT_HOLIDAYS } from "./businessDays";
import { DEFAULT_CALLING_HOURS, type CallingHours } from "./time/callingHours";

export type DialUriScheme = "tel" | "sip" | "callto" | "dialpad";
export type GavionConfig = {
  calling_hours: CallingHours & { timezone_default: string };
  max_attempts: number;
  lease_minutes: number;
  callback_default_time: string;
  dial_uri_scheme: DialUriScheme;
  predial_seconds: number;
  /**
   * Dormant (GAVION small fixes 5, owner decision 2026-10-14): the CRM records no calls and nothing reads `enabled`.
   * Kept for stored configs; recordings made before the removal are purged by the retention job (27_).
   */
  recording: { enabled: boolean; retention_days: number };
  /** ISO weekdays (1 = Monday … 7 = Sunday). */
  business_days: readonly number[];
  holidays: readonly string[];
  scripts: { fr: string; en: string };
  voipms: { sip_server: string; test_number: string };
  /** X1 daily goals; no default here (client-only, read with normalizeGoals in domain/goals.ts). */
  goals?: unknown;
};

export const DEFAULT_GAVION_CONFIG: GavionConfig = Object.freeze({
  calling_hours: {
    ...DEFAULT_CALLING_HOURS,
    timezone_default: "America/Toronto",
  },
  max_attempts: 5,
  lease_minutes: 10,
  callback_default_time: "10:00",
  dial_uri_scheme: "tel",
  predial_seconds: 2,
  recording: { enabled: false, retention_days: 90 },
  business_days: DEFAULT_BUSINESS_DAYS,
  holidays: DEFAULT_HOLIDAYS,
  scripts: {
    fr: "Bonjour, ici {{rep.first_name}} de Gavion. Est-ce que je parle au propriétaire de {{company.name}} ?",
    en: "Hi, this is {{rep.first_name}} from Gavion. Am I speaking with the owner of {{company.name}}?",
  },
  voipms: { sip_server: "montreal1.voip.ms", test_number: "+15148675309" },
}) as GavionConfig;

/** Shallow merge, same semantics as SQL `defaults || stored`. */
export function mergeGavionConfig(stored: unknown): GavionConfig {
  if (!stored || typeof stored !== "object" || Array.isArray(stored))
    return DEFAULT_GAVION_CONFIG;
  return { ...DEFAULT_GAVION_CONFIG, ...(stored as Partial<GavionConfig>) };
}

/** Business-day inputs for `addBusinessDays`/`isBusinessDay`. */
export const calendarOf = (
  config: GavionConfig,
): { businessDays: readonly number[]; holidays: readonly string[] } => ({
  businessDays: config.business_days,
  holidays: config.holidays,
});
